import { createElement } from '../../core/dom/helpers.js';
import { createPortalDropdown } from '../../core/dom/portal-dropdown.js';

// Per-instance counter so multiple mounted switchers never collide on the DOM
// ids referenced by aria-controls / aria-activedescendant.
let instanceCounter = 0;

/**
 * Searchable single-select context switcher (country / language).
 *
 * A trigger `<button>` shows the current selection. Opening it reveals a
 * portaled panel containing a visible search `<input role="combobox">` ABOVE a
 * `role="listbox"` of options, so the search field is discoverable. The search
 * input owns the combobox state (aria-expanded / aria-controls /
 * aria-activedescendant) while it holds focus.
 *
 * @param {HTMLElement} container
 * @param {{
 *   current: import('../../contracts/context.js').AppContext,
 *   options: Array<{ country: string, lang: string, label: string }>,
 *   onChange: (ctx: import('../../contracts/context.js').AppContext) => void
 * }} opts
 * @returns {{ update(ctx: import('../../contracts/context.js').AppContext): void, destroy(): void }}
 */
export function createContextSwitcher(container, { current, options, onChange }) {
  const uid = `context-switcher-${instanceCounter++}`;
  const listboxId = `${uid}-listbox`;
  const panelId = `${uid}-panel`;
  let destroyed = false;
  let isOpen = false;
  let activeIndex = -1;
  /** @type {Array<{ country: string, lang: string, label: string }>} */
  let filtered = options.slice();

  /** @param {{ country: string, lang: string }} ctx */
  function labelFor(ctx) {
    const match = options.find((o) => o.country === ctx.country && o.lang === ctx.lang);
    return match ? match.label : '';
  }

  let currentLabel = labelFor(current);

  // --- Trigger button ---
  // Trigger opens a dialog-style popup (a search input + listbox), so it
  // advertises `haspopup="dialog"` pointing at the panel — NOT "listbox"
  // (which would imply the button itself controls the option list) and NOT
  // "true" (spec-equivalent to "menu", announced as a menu popup). The panel
  // gets role="dialog" + a name below; the search input inside is the real
  // combobox owning the listbox (see below).
  const trigger = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button',
    class: 'context-switcher',
    'aria-haspopup': 'dialog',
    'aria-expanded': 'false',
    'aria-controls': panelId,
  }));
  applyCurrentToTrigger();

  // --- Portaled panel: search input + listbox + status ---
  // role="dialog" + name so the trigger's aria-haspopup="dialog" matches the
  // popup's actual role and AT announces it correctly.
  const panel = createElement('div', {
    class: 'context-switcher__panel',
    id: panelId,
    role: 'dialog',
    'aria-label': 'Select country / language',
  });
  panel.style.display = 'none';

  const search = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'text',
    class: 'context-switcher__search',
    role: 'combobox',
    'aria-haspopup': 'listbox',
    'aria-expanded': 'false',
    'aria-controls': listboxId,
    'aria-label': 'Search country / language',
    autocomplete: 'off',
    placeholder: 'Search…',
  }));

  const listbox = createElement('ul', { class: 'context-switcher__listbox', role: 'listbox', id: listboxId });
  const status = createElement('div', { class: 'context-switcher__status', role: 'status', 'aria-live': 'polite' });
  status.style.display = 'none';
  status.textContent = 'No results';

  panel.appendChild(search);
  panel.appendChild(listbox);
  panel.appendChild(status);

  container.appendChild(trigger);

  const portal = createPortalDropdown({
    anchor: trigger,
    panel,
    onClose: () => close(),
  });

  /** Reflect the committed selection onto the trigger label. */
  function applyCurrentToTrigger() {
    trigger.textContent = currentLabel;
    trigger.setAttribute('aria-label', `Country / language (current: ${currentLabel})`);
  }

  /** @param {number} idx @returns {string} */
  function optionId(idx) {
    return `${uid}-opt-${idx}`;
  }

  function renderOptions() {
    listbox.textContent = '';
    activeIndex = -1;
    search.removeAttribute('aria-activedescendant');

    if (filtered.length === 0) {
      status.style.display = '';
      return;
    }
    status.style.display = 'none';

    filtered.forEach((opt, idx) => {
      const isSelected = opt.country === current.country && opt.lang === current.lang;
      const li = createElement('li', {
        role: 'option',
        class: 'context-switcher__option' + (isSelected ? ' context-switcher__option--selected' : ''),
        'aria-selected': isSelected ? 'true' : 'false',
        id: optionId(idx),
      }, [opt.label]);
      li.addEventListener('mousedown', (e) => {
        // preventDefault so the mousedown doesn't blur the search input before
        // commit() runs. The option is inside the panel, so the portal's
        // document-mousedown handler treats this as an inside click (no
        // outside-close); commit() then closes the panel and focuses the
        // trigger. No suppressNextOutsideClose() — that would arm a flag the
        // (now-closed) portal never consumes, wrongly eating the NEXT open's
        // first outside-click.
        e.preventDefault();
        commit(opt);
      });
      li.addEventListener('mouseover', () => setActive(idx));
      listbox.appendChild(li);
    });
  }

  /** @param {number} idx */
  function setActive(idx) {
    const items = listbox.querySelectorAll('.context-switcher__option');
    items.forEach((item, i) => {
      item.classList.toggle('context-switcher__option--active', i === idx);
    });
    activeIndex = idx;
    if (idx >= 0 && items[idx]) {
      search.setAttribute('aria-activedescendant', items[idx].id || '');
    } else {
      search.removeAttribute('aria-activedescendant');
    }
  }

  function applyFilter() {
    const q = search.value.trim();
    filtered = q === ''
      ? options.slice()
      : options.filter((o) => normalize(o.label).includes(normalize(q)));
    renderOptions();
  }

  function open() {
    if (destroyed || isOpen) return;
    isOpen = true;
    trigger.setAttribute('aria-expanded', 'true');
    search.setAttribute('aria-expanded', 'true');
    portal.open();
    search.value = '';
    applyFilter();
    search.focus();
  }

  /**
   * Hide the panel and reset the transient query/active state; the current
   * selection (trigger label + aria-selected) is left intact. Shared by
   * outside-click (portal onClose), Escape, focusout, and commit(). Does NOT
   * move focus — callers that need focus back on the trigger do it explicitly
   * (commit() and the Escape branch in handleKeydown); outside-click/focusout
   * intentionally leave focus where the user moved it.
   */
  function close() {
    if (!isOpen) return;
    isOpen = false;
    search.value = '';
    trigger.setAttribute('aria-expanded', 'false');
    search.setAttribute('aria-expanded', 'false');
    search.removeAttribute('aria-activedescendant');
    activeIndex = -1;
    portal.close();
  }

  /** @param {{ country: string, lang: string, label: string }} opt */
  function commit(opt) {
    current = { country: opt.country, lang: opt.lang };
    currentLabel = opt.label;
    applyCurrentToTrigger();
    close();
    // Return focus to the visible trigger — both mouse (mousedown preventDefault
    // keeps focus on the now-hidden search input) and keyboard (Enter commits
    // from the search input) would otherwise leave focus trapped inside the
    // closed panel. Focus before onChange so a host re-render keyed off the
    // change can't fight the focus move.
    trigger.focus();
    onChange({ country: opt.country, lang: opt.lang });
  }

  // --- Events ---
  trigger.addEventListener('click', () => {
    if (isOpen) close();
    else open();
  });

  search.addEventListener('input', () => applyFilter());

  // Focus leaving the panel closes it (keyboard Tab-away / programmatic blur).
  // portal-dropdown only observes document mousedown, so this covers the
  // keyboard path. Guard the transient focus move an option mousedown causes.
  search.addEventListener('focusout', (e) => {
    const next = /** @type {Node | null} */ (/** @type {FocusEvent} */ (e).relatedTarget);
    if (next && (panel.contains(next) || trigger.contains(next))) return;
    close();
  });

  /** @param {KeyboardEvent} e */
  function handleKeydown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filtered.length) setActive(Math.min(activeIndex + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filtered.length) setActive(Math.max(activeIndex - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && filtered[activeIndex]) commit(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
      trigger.focus();
    }
  }
  search.addEventListener('keydown', handleKeydown);

  /** @param {import('../../contracts/context.js').AppContext} ctx */
  function update(ctx) {
    current = { country: ctx.country, lang: ctx.lang };
    currentLabel = labelFor(current);
    applyCurrentToTrigger();
    if (isOpen) {
      // host-driven change while open: clear transient query, re-sync selection
      search.value = '';
      applyFilter();
    }
  }

  function destroy() {
    destroyed = true;
    isOpen = false;
    search.removeEventListener('keydown', handleKeydown);
    portal.destroy();
    trigger.remove();
  }

  return { update, destroy };
}

/**
 * Diacritic- and case-insensitive normalization for substring matching.
 * @param {string} s
 * @returns {string}
 */
function normalize(s) {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}
